import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { Close, Download } from '@mui/icons-material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { useCreateDatabase } from '../../../api/bitedb/database/database.hooks';
import { useBulkUploadEntitiesNoCheck } from '../../../api/bitedb/entities/entities.hooks';
import { csvJSON } from '../../../utils/CsvToJson';
import * as XLSX from 'xlsx';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import CreditsAPI from '../../../api/CreditsAPI';
import InsufficientCredits from '../../../pages/credit-plans/InsufficientCredits';
import { useGetStatsByGroupId } from '../../../api/bitedb/stats/stats.hooks';
import {
  ENTITY_SCREENING_LENS_FALLBACK,
  useEntityScreeningLenses,
  withBeneficialOwnershipCompatibility,
} from '../../../hooks/useEntityScreeningLenses';

// Lowercases and strips everything but letters/digits, so the within-file
// duplicate check below is punctuation- and case-insensitive -- "A.V.I.C.",
// "AVIC", "Washington Inc." and "Washington Inc" all collapse to the same
// key instead of silently being treated as different entities. Mirrors
// BiteDBService.normalizeComparisonString (server/api/services/bitedb.service.ts),
// the equivalent DB-side comparison used when uploading into an existing
// (non-empty) database.
const normalizeForDuplicateKey = (value: string): string =>
  value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');

// Notification channels for screening results. The labels are the contract:
// they are stored comma-joined in Database.notifications_email and matched
// case-insensitively by the notification dispatchers, mirroring the options in
// the database's Settings tab (ScreeningSettings).
const NOTIFICATION_OPTIONS: { label: string; description: string }[] = [
  {
    label: 'Email Notification',
    description: 'Email the team when a screening run completes.',
  },
  {
    label: 'In-app Notification',
    description: 'Show screening results in the in-app notification center.',
  },
  {
    label: 'Daily Updates Notification',
    description: 'Include this database in the daily summary digest.',
  },
];

interface ICreateDatabaseModalProps {
  open: boolean;
  handleClose: () => void;
  handleRefresh?: any;
  setTabState?: any;
  setIsScroll?: any;
  onDatabaseCreated?: (databaseId: string) => void;
  onDatabaseUploadFailed?: (databaseId: string) => void;
  onOfferLinkEntities?: (databaseId: string) => void;
  canConfigureEntityAddon?: boolean;
}

const CreateDatabaseModal: React.FC<ICreateDatabaseModalProps> = ({
  handleClose,
  handleRefresh,
  setTabState,
  setIsScroll,
  onDatabaseCreated,
  onDatabaseUploadFailed,
  onOfferLinkEntities,
  canConfigureEntityAddon = false,
}) => {
  const [savingDatabase] = React.useState(false);
  const [databaseName, setDatabaseName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [frequency, setFrequency] = React.useState('Once');
  const [entityAddonEnabled, setEntityAddonEnabled] = React.useState(false);
  // Default to screening against every available list, matching the server's
  // fallback when no lists are supplied.
  const [selectedLists, setSelectedLists] = React.useState<string[]>([
    ...ENTITY_SCREENING_LENS_FALLBACK,
  ]);
  // Default to email + in-app; the daily digest is opt-in. Deselecting all is
  // allowed and disables notifications for the database.
  const [selectedNotifications, setSelectedNotifications] = React.useState<
    string[]
  >(['Email Notification', 'In-app Notification']);
  const { cur_user } = React.useContext<any>(GlobalContext);
  const selectedTeamId = cur_user?.selected_team?.id || '';
  const { data: entityStats } = useGetStatsByGroupId(selectedTeamId);
  const { lenses: entityScreeningLenses, isLoading: isLoadingLenses } =
    useEntityScreeningLenses();
  const hasEditedListsRef = useRef(false);

  useEffect(() => {
    if (!hasEditedListsRef.current) {
      setSelectedLists(entityScreeningLenses);
    }
  }, [entityScreeningLenses]);

  const [step, setStep] = useState<number>(1);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing] = useState<boolean>(false);
  const [status, setStatus] = useState<'idle' | 'creating' | 'uploading'>(
    'idle',
  );
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [cleanData, setCleanData] = useState<any[]>([]);
  const [exportData, setExportData] = useState<any[]>([]);
  const [showDuplicateStep, setShowDuplicateStep] = useState<boolean>(false);
  const [uploadJobId, setUploadJobId] = useState<string | undefined>(undefined);
  const [showInsufficientCredits, setShowInsufficientCredits] =
    useState<boolean>(false);
  const [requiredDatabaseCredits, setRequiredDatabaseCredits] =
    useState<number>(0);
  const [entityCreditsBreakdown, setEntityCreditsBreakdown] = useState<{
    used: number;
    available: number;
    remaining: number;
  }>({
    used: 0,
    available: 0,
    remaining: 0,
  });

  const isCreatingDatabase = useRef(false);
  const hasClosedAfterSubmitRef = useRef(false);
  const activeUploadJobIdRef = useRef<string | undefined>(undefined);
  const pendingDatabaseIdRef = useRef<string | null>(null);
  const hasHandledUploadResultRef = useRef(false);

  const closeToDashboard = useCallback(() => {
    setTabState?.('Dashboard');
    setIsScroll?.(true);
    handleClose();
  }, [handleClose, setIsScroll, setTabState]);

  const closeAndRefresh = useCallback(() => {
    closeToDashboard();
    handleRefresh?.();
  }, [closeToDashboard, handleRefresh]);

  const clearPendingUploadState = useCallback(() => {
    activeUploadJobIdRef.current = undefined;
    pendingDatabaseIdRef.current = null;
    hasHandledUploadResultRef.current = false;
    setUploadJobId(undefined);
  }, []);

  const shouldHandleUploadEvent = useCallback(
    (event: { jobId?: string; databaseId?: string }) => {
      const activeJobId = activeUploadJobIdRef.current;
      if (!activeJobId || event.jobId !== activeJobId) {
        return false;
      }

      const pendingDatabaseId = pendingDatabaseIdRef.current;
      if (
        pendingDatabaseId &&
        event.databaseId &&
        event.databaseId !== pendingDatabaseId
      ) {
        return false;
      }

      return true;
    },
    [],
  );

  // Listen for bulk upload events for this specific job
  useBulkUploadEvents(uploadJobId, {
    onComplete: (result) => {
      if (
        !shouldHandleUploadEvent(result) ||
        hasHandledUploadResultRef.current
      ) {
        return;
      }

      hasHandledUploadResultRef.current = true;
      isCreatingDatabase.current = false;
      setStatus('idle');

      enqueueSnackbar('Upload successful!', { variant: 'success' });
      clearPendingUploadState();
      if (hasClosedAfterSubmitRef.current) {
        handleRefresh?.();
      } else {
        closeAndRefresh();
      }
      hasClosedAfterSubmitRef.current = false;
    },
    onFailed: (result) => {
      if (
        !shouldHandleUploadEvent(result) ||
        hasHandledUploadResultRef.current
      ) {
        return;
      }

      hasHandledUploadResultRef.current = true;
      console.error('[CreateDatabase] Upload failed:', result);
      enqueueSnackbar(
        result.error ||
          'Error during entity upload. Please check the database.',
        { variant: 'error' },
      );
      const failedDatabaseId =
        result.databaseId || pendingDatabaseIdRef.current;
      clearPendingUploadState();
      if (failedDatabaseId) {
        onDatabaseUploadFailed?.(failedDatabaseId);
      }
      isCreatingDatabase.current = false;
      setStatus('idle');
      hasClosedAfterSubmitRef.current = false;
    },
  });

  const handleRowClick = (freq: string) => {
    setFrequency(freq);
  };

  const handleListToggle = (list: string) => {
    hasEditedListsRef.current = true;
    setSelectedLists((prev) =>
      prev.includes(list)
        ? prev.filter((item) => item !== list)
        : [...prev, list],
    );
  };

  const handleNotificationToggle = (option: string) => {
    setSelectedNotifications((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (isProcessing || bulkUploadEntitiesMutation.isPending) {
      return;
    }
    const accptFile = acceptedFiles[0];
    if (!accptFile) {
      return;
    }

    const file = new File([accptFile], accptFile.name, {
      type: accptFile.type,
    });
    setFile(file);

    try {
      await fileHandler(file);
      enqueueSnackbar('File ready for submission!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error processing file. Please ensure it is a valid CSV or XLSX.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setFile(null);
    }
  };

  const fileHandler = async (file: any) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          file.type === 'text/csv' ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.xlsx')
        ) {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
          });
        } else {
          console.error(
            'Mock fileHandler error: Unsupported file type:',
            file.type,
            file.name,
          );
          reject(
            new Error(
              'Mock fileHandler: Unsupported file type. Please upload CSV or XLSX.',
            ),
          );
        }
      }, 500);
    });
  };

  const exportToCSV = (filename: string, data: any, headers: any) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach((item: any) => {
      const values = headers.map((header: any) => {
        const val = item[header] ?? '';
        const escaped = `${val}`.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      enqueueSnackbar(
        'Browser does not support link.download attribute for mock CSV export.',
        { variant: 'error' },
      );
    }
  };

  const fileValidator = (currentFile: File) => {
    if (currentFile.size > 10 * 1024 * 1024) {
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const acceptedExtensions = ['.csv', '.xlsx'];

    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV or Excel (XLSX) file.',
        { variant: 'error' },
      );
      return {
        code: 'file-type-invalid',
        message: 'File type must be one of: .csv, .xlsx',
      };
    }
    return null;
  };

  const handleDeleteFile = () => {
    setFile(null);
    enqueueSnackbar('File removed.', { variant: 'info' });
  };

  const templateHeaders = useMemo(
    () => [
      'name',
      'alias',
      'description',
      'email',
      'phone',
      'address',
      'address1',
      'address2',
      'domain',
      'country_of_origin',
      'category',
      'subCategory',
      'business_unit',
      'supplier_vendor_id',
      'business_registration_number',
      'person_id',
      'manufacturer_identification_number',
    ],
    [],
  );

  const handleDownloadTemplate = () => {
    exportToCSV('entities_template.csv', [], templateHeaders);
    enqueueSnackbar('Template download initiated.', { variant: 'info' });
  };

  const bulkUploadEntitiesMutation = useBulkUploadEntitiesNoCheck();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.csv',
      '.xlsx',
    ],
    multiple: false,
  });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  const readData = (e: any) => {
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        const text = e.target?.result as string;
        return csvJSON(text);
      } else if (file.name.endsWith('.xlsx')) {
        const buf = e.target?.result as ArrayBuffer;
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(ws, { defval: '' });
      }
    }
    return [];
  };

  // Wizard order:
  //   1. Database name
  //   2. File upload
  //   3. Duplicates (conditional)
  //   ... Screening lists (always)
  //   ... Notification preferences (always)
  //   ... Additional Screenings (entity add-on, only for eligible team admins)
  //   ... Screening frequency (always last so it stays adjacent to "Create Database")
  const duplicateStep = 3;
  // First step after the (optional) duplicate step.
  const listsStep = showDuplicateStep ? 4 : 3;
  const notificationsStep = listsStep + 1;
  const entityAddonStep = canConfigureEntityAddon ? notificationsStep + 1 : -1;
  const screeningStep =
    (canConfigureEntityAddon ? entityAddonStep : notificationsStep) + 1;
  const finalStep = screeningStep;

  const handleStep = async (action: string) => {
    if (action === 'increase') {
      if (step === 1 && !databaseName.trim()) {
        enqueueSnackbar('Please enter a database name', { variant: 'error' });
        return;
      }

      if (step === 2) {
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const data = readData(e);

            const groupedData = new Map<string, any[]>();
            const dups: any[] = [];
            const clean: any[] = [];
            const exportList: any[] = [];

            data.forEach((item: any) => {
              const name = item.name || item.Name || '';
              const country =
                item.country_of_origin ||
                item.Country_Of_Origin ||
                item.country ||
                item.Country ||
                '';

              if (!name.trim()) {
                return;
              }

              const key = `${normalizeForDuplicateKey(
                name,
              )}|${normalizeForDuplicateKey(country)}`;

              if (!groupedData.has(key)) {
                groupedData.set(key, []);
              }
              groupedData.get(key)?.push(item);
            });

            groupedData.forEach((items) => {
              if (items.length > 1) {
                exportList.push(...items);
                clean.push(items[0]);
                dups.push(...items.slice(1));
              } else {
                clean.push(items[0]);
              }
            });

            if (dups.length > 0) {
              setDuplicates(dups);
              setCleanData(clean);
              setExportData(exportList);
              setShowDuplicateStep(true);
              setStep(duplicateStep);
            } else {
              setShowDuplicateStep(false);
              setStep(listsStep);
            }
          };
          if (file.name.endsWith('.csv')) {
            reader.readAsText(file);
          } else {
            reader.readAsArrayBuffer(file);
          }
          return;
        }

        setStep(listsStep);
        return;
      }

      if (step === duplicateStep && showDuplicateStep) {
        setStep(listsStep);
        return;
      }

      if (step === listsStep) {
        if (selectedLists.length === 0) {
          enqueueSnackbar('Select at least one screening list', {
            variant: 'error',
          });
          return;
        }
        setStep(notificationsStep);
        return;
      }

      if (step === notificationsStep) {
        setStep(canConfigureEntityAddon ? entityAddonStep : screeningStep);
        return;
      }

      if (step === entityAddonStep && canConfigureEntityAddon) {
        setStep(screeningStep);
        return;
      }

      setStep(Math.min(step + 1, finalStep));
      return;
    }

    if (step === screeningStep) {
      setStep(canConfigureEntityAddon ? entityAddonStep : notificationsStep);
      return;
    }

    if (step === entityAddonStep && canConfigureEntityAddon) {
      setStep(notificationsStep);
      return;
    }

    if (step === notificationsStep) {
      setStep(listsStep);
      return;
    }

    if (step === listsStep) {
      if (showDuplicateStep) {
        setStep(duplicateStep);
        return;
      }
      setStep(2);
      return;
    }

    if (step === duplicateStep && showDuplicateStep) {
      setStep(2);
      return;
    }

    setStep(Math.max(step - 1, 1));
  };

  const handleTypeName = (e: any) => {
    setDatabaseName(e.target.value);
  };

  const handleTypeDescription = (e: any) => {
    setDescription(e.target.value);
  };

  const createDatabaseMutation = useCreateDatabase();

  const getUploadRowCount = useCallback(async (currentFile: File) => {
    if (currentFile.name.endsWith('.csv') || currentFile.type === 'text/csv') {
      const text = await currentFile.text();
      const rows = csvJSON(text);
      return Array.isArray(rows) ? rows.length : 0;
    }

    if (currentFile.name.endsWith('.xlsx')) {
      const buffer = await currentFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
      return Array.isArray(rows) ? rows.length : 0;
    }

    return 0;
  }, []);

  const handleCreateDatabase = async () => {
    if (isCreatingDatabase.current || status !== 'idle') {
      return;
    }

    if (!databaseName) {
      enqueueSnackbar('Please enter a name for the database', {
        variant: 'error',
      });
      return;
    }

    try {
      isCreatingDatabase.current = true;
      setStatus('creating');

      if (file && selectedTeamId) {
        const [rowCount, creditsSummaryResponse] = await Promise.all([
          getUploadRowCount(file),
          CreditsAPI.getCreditsSummary(selectedTeamId),
        ]);
        setRequiredDatabaseCredits(rowCount);

        const dbSize = Number(creditsSummaryResponse?.result?.db_size ?? 0);

        if (!creditsSummaryResponse?.success) {
          throw new Error('Unable to validate database size allowance.');
        }

        const totalUsedRows = Number(entityStats?.total_entities ?? 0);
        const remainingRows = Math.max(dbSize - totalUsedRows, 0);
        setEntityCreditsBreakdown({
          used: totalUsedRows,
          available: dbSize,
          remaining: remainingRows,
        });
        if (totalUsedRows + rowCount > dbSize) {
          setShowInsufficientCredits(true);
          isCreatingDatabase.current = false;
          setStatus('idle');
          return;
        }
      }

      const database = {
        name: databaseName,
        description: description,
        type: 'people & companies', // Hardcoded since it's the only type now
        group_id: cur_user?.selected_team?.id,
        created_by: cur_user?.basics?.id,
        screening_frequency: frequency,
        screening_lists: withBeneficialOwnershipCompatibility(
          selectedLists,
          true,
        ),
        // Ownership is configured independently from BITE Lenses. New entity
        // databases retain the previous create-flow default of enabling it.
        beneficial_ownership_enabled: true,
        // Channel labels, comma-joined -- the same legacy encoding the Settings
        // tab saves and the notification dispatchers parse.
        notifications_email: selectedNotifications.join(','),
        notifications_enabled: selectedNotifications.length > 0,
        entity_add_on_enabled: canConfigureEntityAddon
          ? entityAddonEnabled
          : false,
      };

      const response = await createDatabaseMutation.mutateAsync(database);

      if (!response?.success || !response?.result?.data) {
        throw new Error(
          `Failed to create database: ${response?.result || 'Unknown error'}`,
        );
      }

      const createdDb = response.result.data;
      pendingDatabaseIdRef.current = createdDb.id.toString();

      if (file) {
        onOfferLinkEntities?.(createdDb.id.toString());
      }

      hasClosedAfterSubmitRef.current = true;
      closeToDashboard();

      if (file) {
        onDatabaseCreated?.(createdDb.id.toString());
        setStatus('uploading');

        bulkUploadEntitiesMutation.mutate(
          {
            databaseId: createdDb.id,
            file: file,
            createdBy: cur_user.basics?.id || '',
            teamId: selectedTeamId || '',
          },
          {
            onSuccess: (resp) => {
              if (resp.jobId) {
                activeUploadJobIdRef.current = resp.jobId;
                hasHandledUploadResultRef.current = false;
                setUploadJobId(resp.jobId);
              } else {
                clearPendingUploadState();
                enqueueSnackbar(
                  'Database created, but upload tracking could not be started.',
                  {
                    variant: 'warning',
                  },
                );
                onDatabaseUploadFailed?.(createdDb.id.toString());
                handleRefresh?.();
                hasClosedAfterSubmitRef.current = false;
              }
            },
            onError: (err: any) => {
              if (err?.resp?.data?.error === 'Usage Limit Exceeded') {
                enqueueSnackbar(err.resp.data.message, { variant: 'error' });
              } else {
                enqueueSnackbar('Error during upload. Please try again.', {
                  variant: 'error',
                });
              }
              if (createdDb?.id) {
                onDatabaseUploadFailed?.(createdDb.id.toString());
              }
              clearPendingUploadState();
              isCreatingDatabase.current = false;
              setStatus('idle');
              hasClosedAfterSubmitRef.current = false;
            },
          },
        );

        enqueueSnackbar('Database created! Processing upload...', {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('Database created successfully!', {
          variant: 'success',
        });
        handleRefresh?.();
        clearPendingUploadState();
        isCreatingDatabase.current = false;
        setStatus('idle');
        hasClosedAfterSubmitRef.current = false;
      }
    } catch (error: any) {
      console.error('Error in database creation flow:', error);
      enqueueSnackbar(
        error?.message ||
          'Error during database creation or upload. Please try again.',
        { variant: 'error' },
      );
      clearPendingUploadState();
      isCreatingDatabase.current = false;
      setStatus('idle');
      hasClosedAfterSubmitRef.current = false;
    } finally {
      if (!file) {
        isCreatingDatabase.current = false;
        setStatus('idle');
      }
    }
  };

  return (
    <>
      <div className='login-wizard-container'>
        <button
          onClick={handleClose}
          className='modal-close-button'
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#333';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#666';
          }}
        >
          <Close fontSize='medium' />
        </button>

        <div className='welcome-header'>
          {step === 1 && (
            <div className='welcome-header-text'>Create New Database</div>
          )}
          {step === 2 && (
            <div className='welcome-header-text'>Entities Upload Suppliers</div>
          )}
          {step === duplicateStep && showDuplicateStep && (
            <div className='welcome-header-text'>Duplicate Entities Found</div>
          )}
          {step === listsStep && (
            <div className='welcome-header-text'>Screening Lists</div>
          )}
          {step === notificationsStep && (
            <div className='welcome-header-text'>Notification Preferences</div>
          )}
          {step === screeningStep && (
            <div className='welcome-header-text'>Screening frequency</div>
          )}
          {step === entityAddonStep && canConfigureEntityAddon && (
            <div className='welcome-header-text'>Additional Screenings</div>
          )}
        </div>
        {step === 1 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ minHeight: '420px', height: 'auto' }}
          >
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ paddingTop: '10px' }}
            >
              <p style={{ marginBottom: '0rem' }}>
                Create a new database to organize your suppliers information.
                This will allow you to manage your inventory and run screening
                against potential risks.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: '5px',
              }}
            >
              <div className='database-form-group'>
                <label
                  htmlFor='database-name'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Database Name
                </label>
                <input
                  type='text'
                  id='database-name'
                  placeholder='Enter database name'
                  className='audit-add-modal-cont-body-frame-form-input'
                  value={databaseName}
                  onChange={handleTypeName}
                  style={{ width: '100%' }}
                />
                <p className='modal-section-subtitle'>
                  Choose a descriptive name for your database.
                </p>
              </div>
              <div className='database-form-group'>
                <label
                  htmlFor='database-description'
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                >
                  Description (Optional)
                </label>
                <textarea
                  id='database-description'
                  rows={3}
                  placeholder='Enter a description for your database'
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={{ width: '100%', height: '100px' }}
                  value={description}
                  onChange={handleTypeDescription}
                ></textarea>
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div
            className='team-create-container wizard-step-content'
            style={{ minHeight: '420px', height: 'auto' }}
          >
            <div className='wizard-team-graphic-container'>
              <div className='audit-add-modal-cont-body-frame-form'>
                <div className='audit-add-modal-head-cont-supporting'>
                  <h1>Upload multiple Entities using a CSV file</h1>
                </div>
                <div className='modal-grid'>
                  <div className='modal-section'>
                    <h3 className='modal-section-title'>
                      1. Download Template
                    </h3>
                    <button
                      onClick={handleDownloadTemplate}
                      className='button button-primary button-full-width'
                    >
                      <Download style={{ marginRight: '8px' }} />
                      Download Template
                    </button>
                    <p className='modal-section-subtitle'>
                      Download a .csv template file with example headers.
                    </p>
                  </div>
                  <div className='modal-section'>
                    <h3 className='modal-section-title'>2. Upload File</h3>
                    <div {...getRootProps()} className={dropzoneClassName}>
                      <input {...getInputProps()} />
                      {file ? (
                        <div className='dropzone-content-item'>
                          <svg
                            className='dropzone-icon-success'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={1.5}
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                            />
                          </svg>
                          <p className='dropzone-filename'>{file.name}</p>
                          <p className='dropzone-filesize'>
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFile();
                            }}
                            className='dropzone-remove-button'
                          >
                            Remove File
                          </button>
                        </div>
                      ) : bulkUploadEntitiesMutation.isPending ? (
                        <div className='dropzone-content-item'>
                          Processing file...
                        </div>
                      ) : (
                        <div className='dropzone-content-item'>
                          <p className='dropzone-text'>
                            <span className='dropzone-text-highlight'>
                              Click to upload
                            </span>{' '}
                            or drag and drop
                          </p>
                          <p className='dropzone-subtext'>
                            CSV or XLSX (max 10MB)
                          </p>
                          {isDragReject && (
                            <p className='dropzone-error-text'>
                              Invalid file type or size.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {step === duplicateStep && showDuplicateStep && (
          <div
            className='team-create-container wizard-step-content'
            style={{
              minHeight: '420px',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ paddingTop: '10px', textAlign: 'center' }}
            >
              <p
                style={{
                  marginBottom: '1rem',
                  color: '#d32f2f',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                }}
              >
                {duplicates.length} Duplicate Entities Found
              </p>
              <button
                onClick={() => {
                  const headers = Object.keys(exportData[0] || {});
                  exportToCSV('duplicates.csv', exportData, headers);
                }}
                className='button button-secondary button-full-width'
                style={{
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Download style={{ marginRight: '8px' }} />
                Download Duplicates List
              </button>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  marginTop: '10px',
                  width: '100%',
                }}
              >
                <button
                  onClick={() => {
                    setStep(listsStep);
                  }}
                  className='button button-secondary'
                  style={{ width: '100%', padding: '12px' }}
                >
                  Keep Duplicates
                </button>
                <button
                  onClick={() => {
                    if (cleanData.length > 0) {
                      const headers = Object.keys(cleanData[0]);
                      const csvRows = [];
                      csvRows.push(headers.join(','));
                      cleanData.forEach((item: any) => {
                        const values = headers.map((header: any) => {
                          const escaped = `${item[header]}`.replace(/"/g, '""');
                          return `"${escaped}"`;
                        });
                        csvRows.push(values.join(','));
                      });
                      const csvString = csvRows.join('\r\n');
                      const blob = new Blob([csvString], { type: 'text/csv' });
                      const newFile = new File(
                        [blob],
                        file?.name || 'cleaned_data.csv',
                        { type: 'text/csv' },
                      );
                      setFile(newFile);
                      enqueueSnackbar(
                        'Duplicates removed. Proceeding with unique entities.',
                        { variant: 'success' },
                      );
                      setStep(listsStep);
                    }
                  }}
                  className='button button-primary'
                  style={{
                    width: '100%',
                    backgroundColor: '#d32f2f',
                    borderColor: '#d32f2f',
                    padding: '12px',
                  }}
                >
                  Remove Duplicates
                </button>
              </div>
            </div>
          </div>
        )}
        {step === listsStep && (
          <div
            className='team-create-container wizard-step-content'
            style={{ minHeight: '420px', height: 'auto' }}
          >
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ paddingTop: '10px' }}
            >
              <p style={{ marginBottom: '0rem' }}>
                Choose which lists this database is screened against. All
                entities will be checked against every list you select. You can
                change this later from the database&apos;s Settings tab.
              </p>
            </div>

            <div
              className='database-screening-lens-grid'
              role='group'
              aria-label='Screening lists'
            >
              {isLoadingLenses && (
                <p
                  className='modal-section-subtitle database-screening-lens-grid-status'
                  style={{ margin: 0 }}
                >
                  Loading the latest lenses…
                </p>
              )}
              {entityScreeningLenses.map((list) => {
                const checked = selectedLists.includes(list);
                return (
                  <div
                    key={list}
                    role='checkbox'
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => handleListToggle(list)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleListToggle(list);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      minWidth: 0,
                      width: '100%',
                      borderRadius: 12,
                      border: `1px solid ${
                        checked ? 'var(--action-color)' : '#3E424B'
                      }`,
                      background: checked
                        ? 'linear-gradient(180deg, rgba(72, 149, 239, 0.10) 0%, rgba(72, 149, 239, 0.02) 100%)'
                        : 'var(--bg-primary)',
                      padding: '14px 16px',
                      cursor: 'pointer',
                      transition:
                        'border-color 180ms ease, background 220ms ease',
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        border: `1.5px solid ${
                          checked ? 'var(--action-color)' : '#5B6068'
                        }`,
                        background: checked
                          ? 'var(--action-color)'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition:
                          'background 180ms ease, border-color 180ms ease',
                      }}
                    >
                      {checked && (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='12'
                          height='12'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#FFFFFF'
                          strokeWidth='3'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <polyline points='20 6 9 17 4 12' />
                        </svg>
                      )}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Inter',
                        fontSize: 14,
                        fontWeight: 500,
                        color: checked
                          ? 'var(--text-primary)'
                          : 'var(--text-secondary)',
                        lineHeight: '20px',
                      }}
                    >
                      {list}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedLists.length === 0 && (
              <p
                className='modal-section-subtitle'
                style={{ marginTop: 14, color: '#d32f2f' }}
              >
                Select at least one list to screen against.
              </p>
            )}
          </div>
        )}
        {step === notificationsStep && (
          <div
            className='team-create-container wizard-step-content'
            style={{ minHeight: '420px', height: 'auto' }}
          >
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ paddingTop: '10px' }}
            >
              <p style={{ marginBottom: '0rem' }}>
                Choose how your team is notified about screening results for
                this database. Leave all options off to disable notifications.
                You can change this later from the database&apos;s Settings tab.
              </p>
            </div>

            <div
              role='group'
              aria-label='Notification preferences'
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                marginTop: 18,
              }}
            >
              {NOTIFICATION_OPTIONS.map(({ label, description }) => {
                const checked = selectedNotifications.includes(label);
                return (
                  <div
                    key={label}
                    role='checkbox'
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => handleNotificationToggle(label)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationToggle(label);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      width: '100%',
                      borderRadius: 12,
                      border: `1px solid ${
                        checked ? 'var(--action-color)' : '#3E424B'
                      }`,
                      background: checked
                        ? 'linear-gradient(180deg, rgba(72, 149, 239, 0.10) 0%, rgba(72, 149, 239, 0.02) 100%)'
                        : 'var(--bg-primary)',
                      padding: '14px 16px',
                      cursor: 'pointer',
                      transition:
                        'border-color 180ms ease, background 220ms ease',
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        border: `1.5px solid ${
                          checked ? 'var(--action-color)' : '#5B6068'
                        }`,
                        background: checked
                          ? 'var(--action-color)'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition:
                          'background 180ms ease, border-color 180ms ease',
                      }}
                    >
                      {checked && (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='12'
                          height='12'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#FFFFFF'
                          strokeWidth='3'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        >
                          <polyline points='20 6 9 17 4 12' />
                        </svg>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 14,
                          fontWeight: 500,
                          color: checked
                            ? 'var(--text-primary)'
                            : 'var(--text-secondary)',
                          lineHeight: '20px',
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                          lineHeight: '16px',
                        }}
                      >
                        {description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedNotifications.length === 0 && (
              <p className='modal-section-subtitle' style={{ marginTop: 14 }}>
                Notifications will be disabled for this database.
              </p>
            )}
          </div>
        )}
        {step === screeningStep && (
          <div
            className='team-create-container wizard-step-content'
            style={{ marginBottom: '18px', minHeight: '420px', height: 'auto' }}
          >
            <div className='team-name-form'>
              <div className='team-name-form-label'>Screen My Database:</div>
              <div className='audit-add-modal-cont-body-frame'>
                <div className='bill-container-fit-screen-content-row-selection'>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick('Once')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === 'Once' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === 'Once' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Once</p>
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick('Daily')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === 'Daily' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === 'Daily' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Daily</p>
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick('Weekly')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === 'Weekly' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === 'Weekly' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Weekly</p>
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick('Monthly')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === 'Monthly' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === 'Monthly' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Monthly</p>
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => handleRowClick('Yearly')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          frequency === 'Yearly' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            frequency === 'Yearly' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Yearly</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {step === entityAddonStep && canConfigureEntityAddon && (
          <div
            className='team-create-container wizard-step-content'
            style={{ marginBottom: '18px', minHeight: '420px', height: 'auto' }}
          >
            <div
              className='audit-add-modal-head-cont-supporting'
              style={{ paddingTop: '10px' }}
            >
              <p style={{ marginBottom: '0rem' }}>
                Layer extra verification checks on top of the standard
                screening. Included in your team’s plan.
              </p>
            </div>

            <div
              role='group'
              aria-label='Additional screenings'
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                marginTop: 18,
              }}
            >
              {/* Toggle card */}
              <div
                onClick={() => setEntityAddonEnabled((enabled) => !enabled)}
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: 14,
                  border: `1px solid ${
                    entityAddonEnabled ? 'var(--action-color)' : '#3E424B'
                  }`,
                  background: entityAddonEnabled
                    ? 'linear-gradient(180deg, rgba(72, 149, 239, 0.10) 0%, rgba(72, 149, 239, 0.02) 100%)'
                    : 'var(--bg-primary)',
                  padding: '18px 20px',
                  cursor: 'pointer',
                  transition:
                    'border-color 180ms ease, background 220ms ease, box-shadow 180ms ease',
                  boxShadow: entityAddonEnabled
                    ? '0 0 0 3px rgba(72, 149, 239, 0.12)'
                    : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 16,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 15,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          lineHeight: '20px',
                          letterSpacing: '-0.1px',
                        }}
                      >
                        Business verification
                      </div>
                      <span
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: '#4895EF',
                          background: 'rgba(72, 149, 239, 0.14)',
                          padding: '2px 8px',
                          borderRadius: 999,
                          lineHeight: '16px',
                        }}
                      >
                        On your plan
                      </span>
                    </div>
                    <p
                      className='modal-section-subtitle'
                      style={{
                        marginTop: 6,
                        marginBottom: 0,
                        lineHeight: '20px',
                      }}
                    >
                      Run business verification, address checks, registry
                      lookups and adverse media after every screening of this
                      database.
                    </p>
                  </div>

                  {/* Switch */}
                  <div
                    role='switch'
                    aria-checked={entityAddonEnabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEntityAddonEnabled((enabled) => !enabled);
                    }}
                    style={{
                      position: 'relative',
                      flexShrink: 0,
                      width: 38,
                      height: 22,
                      borderRadius: 999,
                      background: entityAddonEnabled
                        ? 'var(--action-color)'
                        : '#3E424B',
                      transition: 'background 180ms ease',
                      marginTop: 2,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 2,
                        left: entityAddonEnabled ? 18 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#FFFFFF',
                        transition: 'left 180ms ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                      }}
                    />
                  </div>
                </div>

                {/* Included checks */}
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 18px',
                  }}
                >
                  {[
                    'Domain age & reputation',
                    'Email & phone verification',
                    'Address validation',
                    'Website quality signals',
                    'Business registry lookups',
                    'Adverse media monitoring',
                  ].map((item) => (
                    <div
                      key={item}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontFamily: 'Inter',
                        fontSize: 12.5,
                        color: entityAddonEnabled
                          ? 'var(--text-primary)'
                          : 'var(--text-secondary)',
                        lineHeight: '18px',
                        transition: 'color 180ms ease',
                      }}
                    >
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: entityAddonEnabled
                            ? '#4895EF'
                            : '#5B6068',
                          flexShrink: 0,
                          transition: 'background 180ms ease',
                        }}
                      />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Charges callout */}
              <div
                role='note'
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid rgba(245, 158, 11, 0.28)',
                  background: 'rgba(245, 158, 11, 0.08)',
                }}
              >
                <div
                  aria-hidden
                  style={{
                    flexShrink: 0,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'rgba(245, 158, 11, 0.18)',
                    color: '#F59E0B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Inter',
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1,
                    marginTop: 1,
                  }}
                >
                  !
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'Inter',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#F8C266',
                      lineHeight: '18px',
                    }}
                  >
                    Additional charges may apply
                  </div>
                  <div
                    style={{
                      marginTop: 2,
                      fontFamily: 'Inter',
                      fontSize: 12.5,
                      color: 'var(--text-secondary)',
                      lineHeight: '18px',
                    }}
                  >
                    Each enabled check is billed per entity, per screening run.
                    You can turn this off later from the database&apos;s
                    Settings tab.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step <= finalStep && (
          <div className='wizard-body-container'>
            <div className='wizard-body-text-group'></div>
          </div>
        )}

        {step <= finalStep && (
          <div className='wizard-pagination-group'>
            <div
              className={`wizard-page-dot${step === 1 ? '-selected' : ''}`}
              onClick={() => setStep(1)}
            ></div>
            <div
              className={`wizard-page-dot${step === 2 ? '-selected' : ''}`}
              onClick={() => {
                if (!databaseName.trim()) {
                  enqueueSnackbar('Please enter a database name', {
                    variant: 'error',
                  });
                  return;
                }
                setStep(2);
              }}
            ></div>
            {showDuplicateStep && (
              <div
                className={`wizard-page-dot${
                  step === duplicateStep ? '-selected' : ''
                }`}
                onClick={() => {
                  if (step >= duplicateStep) {
                    setStep(duplicateStep);
                  }
                }}
              ></div>
            )}
            <div
              className={`wizard-page-dot${
                step === listsStep ? '-selected' : ''
              }`}
              onClick={() => {
                if (!databaseName.trim()) {
                  enqueueSnackbar('Please enter a database name', {
                    variant: 'error',
                  });
                  return;
                }
                if (step >= 2) {
                  setStep(listsStep);
                }
              }}
            ></div>
            <div
              className={`wizard-page-dot${
                step === notificationsStep ? '-selected' : ''
              }`}
              onClick={() => {
                if (!databaseName.trim()) {
                  enqueueSnackbar('Please enter a database name', {
                    variant: 'error',
                  });
                  return;
                }
                if (step >= 2 && selectedLists.length > 0) {
                  setStep(notificationsStep);
                }
              }}
            ></div>
            {canConfigureEntityAddon && (
              <div
                className={`wizard-page-dot${
                  step === entityAddonStep ? '-selected' : ''
                }`}
                onClick={() => {
                  if (!databaseName.trim()) {
                    enqueueSnackbar('Please enter a database name', {
                      variant: 'error',
                    });
                    return;
                  }
                  if (step >= 2 && selectedLists.length > 0) {
                    setStep(entityAddonStep);
                  }
                }}
              ></div>
            )}
            <div
              className={`wizard-page-dot${
                step === screeningStep ? '-selected' : ''
              }`}
              onClick={() => {
                if (!databaseName.trim()) {
                  enqueueSnackbar('Please enter a database name', {
                    variant: 'error',
                  });
                  return;
                }
                if (step >= 2 && selectedLists.length > 0) {
                  setStep(screeningStep);
                }
              }}
            ></div>
          </div>
        )}
        {step <= finalStep && (
          <div className='wizard-btn-group'>
            <div className='wizard-btn-container'>
              <div className='wizard-btn-dropdown'>
                {savingDatabase ? (
                  <div className='wizard-back-btn-disabled'>
                    <div className='wizard-btn-text-padding'>
                      <div className='wizard-back-btn-text-disabled'>
                        {step === 1 ? 'Close' : 'Back'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={
                      step === 1
                        ? 'wizard-back-btn'
                        : `wizard-back-btn${step > 1 ? '' : '-disabled'}`
                    }
                    onClick={
                      step === 1 ? handleClose : () => handleStep('decrease')
                    }
                  >
                    <div className='wizard-btn-text-padding'>
                      <div
                        className={
                          step === 1
                            ? 'wizard-back-btn-text'
                            : `wizard-back-btn-text${
                                step > 1 ? '' : '-disabled'
                              }`
                        }
                      >
                        {step === 1 ? 'Close' : 'Back'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {step === 1 && (
                <div
                  className='wizard-primary-btn'
                  onClick={() => handleStep('increase')}
                >
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              )}
              {step === 2 && (
                <>
                  <div
                    className='wizard-primary-btn'
                    onClick={() => handleStep('increase')}
                  >
                    <div className='wizard-next-btn-text'>
                      {file ? 'Next' : 'Skip'}
                    </div>
                  </div>
                </>
              )}
              {step === duplicateStep && showDuplicateStep && <></>}
              {step === listsStep && (
                <div
                  className='wizard-primary-btn'
                  onClick={() => handleStep('increase')}
                >
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              )}
              {step === notificationsStep && (
                <div
                  className='wizard-primary-btn'
                  onClick={() => handleStep('increase')}
                >
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              )}
              {step === entityAddonStep && canConfigureEntityAddon && (
                <div
                  className='wizard-primary-btn'
                  onClick={() => handleStep('increase')}
                >
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              )}
              {step === finalStep && (
                <div
                  className='wizard-primary-btn'
                  onClick={handleCreateDatabase}
                >
                  <div className='wizard-next-btn-text'>
                    {status === 'creating' || status === 'uploading'
                      ? 'Creating...'
                      : 'Create Database'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <InsufficientCredits
        teamId={cur_user?.selected_team?.id || ''}
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        type='entity'
        requiredCredits={requiredDatabaseCredits}
        usedCredits={entityCreditsBreakdown.used}
        availableCredits={entityCreditsBreakdown.available}
        remainingCredits={entityCreditsBreakdown.remaining}
      />
    </>
  );
};

export default CreateDatabaseModal;
